import React, { useState } from 'react';
import { Card, Carousel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBed, faBath, faRuler, faCalendar, faGraduationCap, faStar, faMapMarkerAlt, faBuilding } from '@fortawesome/free-solid-svg-icons';
import './PropertyHoverCard.css';

interface PropertyHoverCardProps {
  property: any;
  x: number;
  y: number;
  thetaInsight?: string;
}

const PropertyHoverCard: React.FC<PropertyHoverCardProps> = ({ property, x, y, thetaInsight }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Adjust position to prevent card from going off screen
  const adjustedX = x + 360 > window.innerWidth ? x - 360 : x + 20;
  const adjustedY = y + 300 > window.innerHeight ? y - 300 : y - 20;
  
  // Check for images in various property structures
  const propertyImages = Array.isArray(property.images) ? property.images : 
                        Array.isArray(property.thumbnails) ? property.thumbnails : [];
  const hasImages = propertyImages.length > 0;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <Card 
      className="property-hover-card"
      style={{ 
        position: 'fixed',
        left: `${adjustedX}px`, 
        top: `${adjustedY}px`,
        zIndex: 1000
      }}
    >
      <div className="hover-card-image">
        {hasImages && propertyImages.length > 1 ? (
          <Carousel
            activeIndex={activeIndex}
            onSelect={setActiveIndex}
            interval={3000}
            indicators={false}
            controls={propertyImages.length > 1}
            fade={false} // Disable fade transition
            slide={false} // Disable slide animation for smoother experience
          >
            {propertyImages.slice(0, 3).map((image: string, index: number) => (
              <Carousel.Item key={index}>
                <img 
                  src={image} 
                  alt={`${property.address?.street || 'Property'} - Image ${index + 1}`}
                  loading="eager" // Load images immediately
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/320x200/1f2937/ffffff?text=No+Image';
                  }}
                />
              </Carousel.Item>
            ))}
          </Carousel>
        ) : (
          <img 
            src={hasImages ? propertyImages[0] : 'https://via.placeholder.com/320x200/1f2937/ffffff?text=No+Image'} 
            alt={property.address?.street || 'Property'}
            loading="eager"
            onError={(e) => {
              e.currentTarget.src = 'https://via.placeholder.com/320x200/1f2937/ffffff?text=No+Image';
            }}
          />
        )}
        
        {/* Overlay badges */}
        <div className="hover-badges">
          {property.daysOnMarket && property.daysOnMarket <= 3 && (
            <span className="hot-badge">🔥 HOT</span>
          )}
          {property.daysOnMarket && property.daysOnMarket > 3 && property.daysOnMarket <= 7 && (
            <span className="new-listing-badge">NEW</span>
          )}
        </div>
        
        {/* Photo counter */}
        {propertyImages.length > 1 && (
          <div className="photo-counter">
            {activeIndex + 1} / {Math.min(propertyImages.length, 3)}
          </div>
        )}
        
        {/* Property specs overlay */}
        <div className="hover-specs-overlay">
          <span><FontAwesomeIcon icon={faBed} /> {property.bedrooms || 0}</span>
          <span><FontAwesomeIcon icon={faBath} /> {property.bathrooms || 0}</span>
          <span><FontAwesomeIcon icon={faRuler} /> {property.sqft?.toLocaleString() || 'N/A'}</span>
        </div>
      </div>
      
      <Card.Body>
        <div className="match-score">
          <div className="match-percentage">
            {Math.floor(Math.random() * 20) + 75}%
          </div>
          <div className="match-label">Match</div>
        </div>
        
        <div className="price-row">
          <h4 className="price">{formatPrice(property.price)}</h4>
          {property.priceChange && (
            <span className="price-change">
              ↓ ${Math.abs(property.priceChange).toLocaleString()}
            </span>
          )}
        </div>
        
        <p className="address">
          <FontAwesomeIcon icon={faMapMarkerAlt} className="address-icon" />
          <span>
            {property.street || property.address?.street || 'Address not available'}<br />
            <span className="city-state">
              {property.city || property.address?.city || 'City'}, {property.state || property.address?.state || 'AZ'} {property.zip || property.address?.zip || ''}
            </span>
          </span>
        </p>
        
        <div className="property-meta">
          {property.schoolRating && (
            <span className="school-rating">
              <FontAwesomeIcon icon={faGraduationCap} /> 
              School: {property.schoolRating}/10
            </span>
          )}
          {property.daysOnMarket !== undefined && (
            <span className="days-on-market">
              <FontAwesomeIcon icon={faCalendar} /> 
              {property.daysOnMarket} days
            </span>
          )}
        </div>
        
        {/* Listing Info */}
        {(property.listingOffice || property.listingAgent?.officeName) && (
          <div className="listing-info">
            <FontAwesomeIcon icon={faBuilding} className="office-icon" />
            <span>{property.listingOffice || property.listingAgent?.officeName}</span>
          </div>
        )}
        
        {/* MLS Number */}
        <div className="mls-info">
          MLS# {property.mlsNumber || 'N/A'}
        </div>
        
        {thetaInsight && (
          <div className="theta-insight">
            <FontAwesomeIcon icon={faStar} className="sparkle" />
            <p>{thetaInsight}</p>
          </div>
        )}
        
        <div className="hover-hint">Click for details →</div>
      </Card.Body>
    </Card>
  );
};

export default PropertyHoverCard;